import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';

interface AppHeaderProps {
  onLogout?: () => void;
}

/**
 * Top application header.
 * Displays app name, logged-in user info, and a logout button.
 */
export const AppHeader = ({ onLogout }: AppHeaderProps): React.ReactElement => {
  const auth = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = (): void => {
    setShowUserMenu(false);
    if (onLogout) onLogout();
  };

  // Derive user display name and initials from OIDC profile
  const userProfile = auth.user?.profile;
  const userName: string =
    (userProfile?.name as string | undefined) ??
    (userProfile?.preferred_username as string | undefined) ??
    (userProfile?.email as string | undefined) ??
    'User';
  const initials = userName
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const userEmail = userProfile?.email as string | undefined;
  const realmRoles = (userProfile?.realm_access as { roles?: string[] } | undefined)?.roles ?? [];

  return (
    <header className="sticky top-0 z-50 flex h-14 items-center gap-2 border-b border-slate-200 bg-white px-2 shadow-sm sm:gap-4 sm:px-6">
      {/* App name */}
      <span className="select-none text-xl font-semibold tracking-tight text-slate-800">AQE FISO</span>

      <div className="flex-1" />

      {/* User avatar + dropdown */}
      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setShowUserMenu((v) => !v)}
          className="flex cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent"
          aria-label="User menu"
        >
          <span className="hidden font-medium text-slate-600 md:inline">{userName}</span>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-600">
            {initials}
          </div>
        </button>

        {showUserMenu && (
          <div className="fade-in card absolute right-0 top-full mt-1 min-w-56 p-1.5 shadow-lg">
            <div className="border-b border-slate-100 px-3 py-2">
              <div className="font-semibold">{userName}</div>
              {userEmail && <div className="text-xs text-slate-500">{userEmail}</div>}
              {realmRoles.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {realmRoles.map((role) => (
                    <span key={role} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      {role}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {onLogout && (
              <button
                type="button"
                onClick={handleLogout}
                className="mt-1 w-full rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                Log out
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
